import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { Mindmap, MindmapNode } from "@/lib/mindmaps/types";
import { cn } from "@/lib/utils";

/** Renders the supplied mindmap tree as-is. No content is generated here. */

function Branch({ node, depth }: { node: MindmapNode; depth: number }) {
  const children = node.children ?? [];
  const [open, setOpen] = useState(depth < 1);
  const hasChildren = children.length > 0;

  return (
    <li className="relative">
      <div
        className={cn(
          "flex items-start gap-2 rounded-2xl border border-white/10 bg-card/60 px-3.5 py-2.5",
          depth === 0 && "bg-primary/5",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "mt-1.5 size-1.5 shrink-0 rounded-full",
            depth === 0 ? "bg-primary" : "bg-muted-foreground/60",
          )}
        />
        <p
          className={cn(
            "min-w-0 flex-1 text-[13px] leading-snug",
            depth === 0 ? "font-bold" : depth === 1 ? "font-semibold" : "text-muted-foreground",
          )}
        >
          {node.name}
        </p>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
            className="shrink-0 rounded-full p-1 text-muted-foreground transition-transform active:scale-90"
          >
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        ) : null}
      </div>
      {hasChildren && open ? (
        <ul className="ml-3 mt-2 space-y-2 border-l border-white/10 pl-3">
          {children.map((child, i) => (
            <Branch key={`${child.name}-${i}`} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function MindmapView({ map }: { map: Mindmap }) {
  if (!map.nodes.length) {
    return <p className="text-[13px] text-muted-foreground">This mindmap has no branches.</p>;
  }
  return (
    <ul className="space-y-2.5">
      {map.nodes.map((node, i) => (
        <Branch key={`${node.name}-${i}`} node={node} depth={0} />
      ))}
    </ul>
  );
}
